import type { FlowState } from '../providers/dynamicExperienceReducer';
import type { Option } from '../types/types';

/**
 * Resolves placeholders in a question title string.
 * Supports {{questionId}} syntax to reference previous question answers.
 */
export const resolveQuestionTitle = (
  title: string,
  flowState: FlowState,
): string => {
  if (!title) return '';

  const placeholderRegex = /\{\{([^}]+)\}\}/g;
  let resolvedTitle = title;

  const matches = Array.from(title.matchAll(placeholderRegex));
  for (const match of matches) {
    const questionId = match[1];
    const stepData = flowState[questionId];

    let replacementValue = '';

    if (stepData && stepData.option) {
      if (Array.isArray(stepData.option)) {
        replacementValue = stepData.option
          .map((opt: Option) => opt.text || opt.value)
          .join(', ');
      } else {
        const option = stepData.option as Option;
        replacementValue = (option.text || option.value) as string;
      }
    }

    resolvedTitle = resolvedTitle.replace(match[0], replacementValue);
  }

  return resolvedTitle;
};

/**
 * Resolves placeholders in dynamic filter configuration.
 * Supports {{questionId}} syntax to reference previous question answers.
 */
export const resolveDynamicFilters = (
  filters: { type?: string; subject?: string; filterKey?: string } | undefined,
  flowState: FlowState,
): { type?: string; subject?: string } => {
  if (!filters) return {};

  const resolved: { type?: string; subject?: string } = {};

  const resolveValue = (value: string | undefined): string | undefined => {
    if (!value) return value;

    const placeholderRegex = /\{\{([^}]+)\}\}/g;
    let resolvedValue = value;

    const matches = Array.from(value.matchAll(placeholderRegex));
    for (const match of matches) {
      const questionId = match[1];
      const stepData = flowState[questionId];

      if (stepData && stepData.option) {
        let replacementValue = '';

        if (Array.isArray(stepData.option)) {
          replacementValue = stepData.option
            .map((opt: Option) => opt.value || opt.text)
            .join(',');
        } else {
          const option = stepData.option as Option;
          replacementValue = (option.value || option.text) as string;
        }

        resolvedValue = resolvedValue.replace(match[0], replacementValue);
      }
    }

    return resolvedValue;
  };

  resolved.type = resolveValue(filters.type);
  resolved.subject = resolveValue(filters.subject);

  return resolved;
};
